/**
 *  cst-gift-cart — the gift-with-purchase reconciler.
 *
 *  Plan: docs/plans/2026-09-19-gwp-auto-add-plan.md §5 step 2.3.
 *
 *  A module singleton on window.__cstGiftCart, NOT a custom element: /cart renders two
 *  <cst-rewards-bar> instances, and a custom element would run this logic once per instance.
 *  Loaded from layout/theme.liquid on EVERY page, ungated by settings.rewards_bar_enable — with
 *  the gift switch off the config says `active:false` and this file removes every gift line on the
 *  shopper's next page load, wherever they are. That ungated remover IS the rollback (plan §8).
 *
 *  It owns no UI. Per pass it reconciles the cart's gift lines against the config: removals and
 *  quantity clamps first, in ONE /cart/update.js addressed by line KEY; adds second, in ONE
 *  /cart/add.js; then it re-renders the theme's own cart sections from the bundled `sections` of
 *  the last response. It never dispatches cart:refresh (that re-enters the theme's cart code and
 *  every other listener), never writes cart attributes or line properties, and never intercepts a
 *  click. Gift lines are excluded from the eligible total, so one pass always converges.
 *
 *  COVER INSERT — when the config says `dynamic: true`, the tier set depends on cart CONTENTS (the
 *  tags only Liquid can read), so every pass after the first re-fetches the config through
 *  sections/cst-gift-config.liquid and judges the cart against that fresh copy. One GET per cart
 *  event, only while the cover feature is on; with it off this file behaves exactly as before.
 *
 *  TESTING ENTRY POINT (owner ruling R11) — window.__cstGiftCart.configure(cfg) replaces the
 *  config and re-runs, so the cart mechanics can be exercised on a draft theme with an EXISTING
 *  product standing in for a gift, supplied only from the browser console:
 *
 *    __cstGiftCart.configure({ active: true, allowNonFreeGift: true,
 *      tiers: [{ id: 'tier-2', label: 'Stand-in', variantId: 1234567890, threshold: 8000 }] })
 *
 *  `allowNonFreeGift` is the ONLY way to skip the "a gift line must be free" rule, and Liquid
 *  never emits that field — readConfig() forces it false — so no server render can take that path.
 */
(function () {
  // The theme editor renders the storefront against the MERCHANT's own cart. Phase 4 step 1 is
  // "Customize → tick Show free gift rewards → Save", so without this the switch would add a gift
  // to her cart the moment she ticks it. (Shopify.designMode is the documented editor test.)
  if (window.Shopify && window.Shopify.designMode) return;

  // The gift SKU marker is a PREFIX, not a substring (plan §5 "SKU (uppercase, leading GIFT-)").
  // `contains` would classify an ordinary paid product such as WRAP-GIFT-BLUE as a gift and remove
  // it from the shopper's cart.
  const GIFT_SKU = 'GIFT-';
  // The section that re-renders the config on demand (sections/cst-gift-config.liquid).
  const CONFIG_SECTION = 'cst-gift-config';
  // Spend that never counts toward a threshold — mirrors cst-rewards-bar.js:130.
  const EXCLUDED_SKU_PARTS = ['DESTASH', 'ADVENT'];
  // Used only when neither cart owner exists (cart drawer disabled AND not on /cart). Identical to
  // Cart.getSectionsToRender (assets/cart.js:50) and CartDrawer.getSectionsToRender (app.js:551).
  const FALLBACK_SECTIONS = [
    { id: 'Cart', section: 'main-cart', selector: '.thb-cart-form' },
    { id: 'Cart-Drawer', section: 'cart-drawer', selector: '.cart-drawer-inner' },
    { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-item-count' }
  ];

  const routes = (window.theme && window.theme.routes) || {};
  const CART_URL = `${routes.cart_url || '/cart'}.js`;
  const ADD_URL = `${routes.cart_add_url || '/cart/add'}.js`;
  const UPDATE_URL = `${routes.cart_update_url || '/cart/update'}.js`;

  let config = null;
  let chain = Promise.resolve();
  let running = false;
  let rerun = false;
  // The page-load config still describes the cart: true for the first pass only.
  let snapshotValid = true;
  // Page-lifetime circuit breaker: a gift line came back priced, so stop adding gifts entirely.
  let breaker = false;
  // Bumped by every event that follows a cart re-render by the theme. A pass whose generation is
  // stale by the time its response lands must not paint markup computed before that render.
  let cartGen = 0;
  // G5: a pass discarded its paint (see pass()); the next quiet moment owes the surfaces a repaint.
  let needsFreshPaint = false;
  // R11: a console configure() owns the config for the rest of the page load — a dynamic re-fetch
  // must never overwrite a stand-in test config with the server's.
  let consoleOverride = false;
  // Tiers whose add was rejected (422 = sold out) or whose line came back priced. One starved tier
  // must not turn every later trigger into a retry storm.
  const failedTiers = new Set();

  // ── config ──────────────────────────────────────────────────────────────────────────────────

  function normalise(cfg) {
    const raw = cfg && typeof cfg === 'object' ? cfg : {};
    return {
      active: raw.active === true,
      dynamic: raw.dynamic === true,
      total: Number(raw.total) || 0,
      hasMerch: raw.hasMerch === true,
      tiers: (Array.isArray(raw.tiers) ? raw.tiers : [])
        .filter((tier) => tier && tier.variantId)
        .map((tier) => ({
          id: String(tier.id),
          label: tier.label || '',
          variantId: Number(tier.variantId),
          threshold: Number(tier.threshold) || 0,
          // How many of this gift the cart is owed: the cover insert is one per cover (owner,
          // 2026-09-22); a spend tier carries no quantity and is always one.
          quantity: Math.max(1, Math.floor(Number(tier.quantity)) || 1)
        })),
      lines: (Array.isArray(raw.lines) ? raw.lines : []).filter((line) => line && line.key).map(toLine),
      strings: raw.strings || {},
      allowNonFreeGift: raw.allowNonFreeGift === true
    };
  }

  function parseConfig(text) {
    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch (e) {
      return null;
    }
    if (!parsed || typeof parsed !== 'object') return null;
    // A server render can never take the console test path.
    parsed.allowNonFreeGift = false;
    return normalise(parsed);
  }

  function readConfig() {
    const el = document.getElementById('cst-gift-config');
    return el ? parseConfig(el.textContent) : null;
  }

  // The re-render the config never gets otherwise: theme.liquid is a layout, and no cart paint
  // touches it. NO Accept header — with `Accept: application/json` Shopify content-negotiates the
  // GET into the cart JSON and ignores `sections` (observed live 2026-09-20; cst-cart-requests.js
  // at the reconcile pair). Anything but a section map carrying our id is a failure, and a failure
  // is null.
  function fetchConfig() {
    return fetch(`${window.location.pathname}?sections=${CONFIG_SECTION}`)
      .then((response) => (response.ok ? response.json() : null))
      .then((map) => {
        const html = map && typeof map === 'object' ? map[CONFIG_SECTION] : null;
        if (!html) return null;
        const el = new DOMParser().parseFromString(html, 'text/html').querySelector('#cst-gift-config');
        return el ? parseConfig(el.textContent) : null;
      })
      .catch(() => null);
  }

  // ── cart state ──────────────────────────────────────────────────────────────────────────────

  // One shape for a gift line, whether it came from the config, /cart.js or an add response.
  function toLine(item) {
    return {
      key: item.key,
      variantId: Number(item.variantId === undefined ? item.variant_id : item.variantId),
      quantity: Number(item.quantity === undefined ? 1 : item.quantity),
      price: Number(item.price === undefined ? item.final_line_price : item.price) || 0,
      title: item.title || item.product_title || ''
    };
  }

  // Ruling R11: a line is a gift if its SKU carries the marker OR its variant is a configured
  // tier — the second half is what lets a stand-in product be tested from the console. `cfg` is
  // passed in, never read from module state, so one pass always judges against one config.
  function isGift(cfg, sku, variantId) {
    if (String(sku || '').toUpperCase().startsWith(GIFT_SKU)) return true;
    const id = Number(variantId);
    return cfg.tiers.some((tier) => tier.variantId === id);
  }

  function stateFromCart(cfg, cart) {
    const items = cart && Array.isArray(cart.items) ? cart.items : [];
    const lines = [];
    let total = 0;
    let hasMerch = false;
    items.forEach((item) => {
      const sku = String(item.sku || '').toUpperCase();
      if (isGift(cfg, sku, item.variant_id)) {
        lines.push(toLine(item));
        return; // one exit governs both the total and has-merch, as the Liquid `continue` does
      }
      if (EXCLUDED_SKU_PARTS.some((part) => sku.includes(part))) return;
      total += item.final_line_price;
      if (item.requires_shipping) hasMerch = true;
    });
    return { total, hasMerch, lines };
  }

  function getCart() {
    return fetch(CART_URL, { headers: { Accept: 'application/json' } }).then((response) => response.json());
  }

  function post(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    })
      .then((response) =>
        response.json().then(
          (data) => ({ ok: response.ok, status: response.status, data }),
          () => ({ ok: response.ok, status: response.status, data: null })
        )
      )
      .catch(() => ({ ok: false, status: 0, data: null }));
  }

  // 4xx means this variant will not go in for the rest of the page load (422 = sold out). A
  // dropped connection (status 0) or a 5xx is transient — leave the tier alone so the next trigger
  // can try again, instead of starving a gift for a shopper who hit one bad request.
  function markFailed(tier, status) {
    if (status >= 400 && status < 500) failedTiers.add(tier.id);
  }

  // ── rendering ───────────────────────────────────────────────────────────────────────────────

  // Always the constant three-target union. Asking a cart owner for its list was a v3 habit:
  // window.__cart no longer exists on this theme and <cart-drawer> renders on EVERY page, so the
  // owner it found was CartDrawer, whose list omits main-cart — a gift add or removal then never
  // repainted /cart. Constant beats coupling on the money path.
  function renderTargets() {
    return FALLBACK_SECTIONS;
  }

  // Same replacement the theme does (cart.js:169), but every step is guarded: a missing or partial
  // section leaves the live markup alone instead of blanking the cart.
  function render(targets, sections) {
    if (!sections) return;
    let drawerReplaced = false;
    targets.forEach((target) => {
      const container = document.getElementById(target.id);
      const html = sections[target.section];
      if (!container || !html) return;
      const replacement = new DOMParser().parseFromString(html, 'text/html').querySelector(target.selector);
      if (!replacement) return;
      const elementToReplace = container.querySelector(target.selector) || container;
      elementToReplace.innerHTML = replacement.innerHTML;
      if (target.id === 'Cart-Drawer') drawerReplaced = true;
    });
    // The drawer rebinds its note, discount and free-shipping controls after every replacement
    // (app.js:659 in v3; app.js:894-896 in v8, which added the second and third). ONLY when the
    // drawer target was actually replaced: all three addEventListener unconditionally, so
    // rebinding after a skipped paint stacks a second note-save handler on live DOM. Optional
    // chaining keeps this a no-op on a theme that has no such methods.
    if (drawerReplaced) {
      const cartDrawer = document.querySelector('cart-drawer');
      cartDrawer?.notesToggle?.();
      cartDrawer?.discountToggle?.();
      cartDrawer?.updateFreeShipping?.();
    }
    // destash-restriction.js injects its Restore / mixed-cart banners INTO the drawer markup we
    // just replaced, and it only rebuilds them on a cart event — which this file deliberately
    // never dispatches. DOM-only redraw from its existing state: no cart request.
    window.__destashRedraw?.();
  }

  // The rewards bar's persistent live region (cst-rewards-bar.js:371) — it sits OUTSIDE the
  // replaced cart markup, so an announcement survives the re-render.
  function announce(text) {
    if (!text) return;
    let live = document.getElementById('cst-rewards-live');
    if (!live) {
      live = document.createElement('div');
      live.id = 'cst-rewards-live';
      live.className = 'visually-hidden';
      live.setAttribute('aria-live', 'polite');
      document.body.appendChild(live);
    }
    live.textContent = text;
  }

  function message(template, reward) {
    if (!template) return '';
    return String(template).replace('%%REWARD%%', reward || '');
  }

  // ── seamless paint ──────────────────────────────────────────────────────────────────────────

  // Owner request 2026-09-22: the gift landing a beat after the drawer opened read as a stutter —
  // the whole list jumped. A same-document view transition turns this file's repaint into a glide:
  // for the length of the transition the cart lines on screen are named by line KEY, so a line that
  // moves slides to its new place and a new gift fades in. Names go ONLY on rows that are visible
  // inside their scroll box — a named element escapes its ancestors' clipping while the transition
  // runs, so an off-screen row of a long cart would flash over the drawer footer — and they are
  // cleared when it ends, so no other transition on the page (facets, pagination) captures them.
  // NOT gated on the theme's `animations` setting (app.js withViewTransition): that switch governs
  // decorative scroll reveals and is OFF on MAIN. Reduced motion and older browsers paint instantly,
  // exactly as before; any failure in here falls back to the instant paint.
  const ROW_SOURCES = [
    { surface: 'd', selector: '#Cart-Drawer .product-cart-item[data-key]', key: (row) => row.dataset.key },
    { surface: 'm', selector: '#Cart [id^="CartItem-"][data-product-id]', key: (row) => row.dataset.productId }
  ];

  function clipBox(el) {
    for (let node = el.parentElement; node && node !== document.body; node = node.parentElement) {
      const overflow = window.getComputedStyle(node).overflowY;
      if (overflow === 'auto' || overflow === 'scroll' || overflow === 'hidden') return node.getBoundingClientRect();
    }
    return { top: 0, left: 0, bottom: window.innerHeight, right: window.innerWidth };
  }

  function onScreen(el) {
    const rect = el.getBoundingClientRect();
    if (!rect.width || !rect.height) return false;
    const box = clipBox(el);
    return (
      rect.bottom > Math.max(box.top, 0) &&
      rect.top < Math.min(box.bottom, window.innerHeight) &&
      rect.right > box.left &&
      rect.left < box.right
    );
  }

  function nameRows() {
    const named = [];
    if (!document.querySelectorAll) return named;
    ROW_SOURCES.forEach((source) => {
      document.querySelectorAll(source.selector).forEach((row) => {
        const key = source.key(row);
        if (!key || !onScreen(row)) return;
        row.style.viewTransitionName = `cst-${source.surface}-${String(key).replace(/[^A-Za-z0-9_-]/g, '-')}`;
        named.push(row);
      });
    });
    return named;
  }

  function clearNames(rows) {
    rows.forEach((row) => {
      row.style.viewTransitionName = '';
    });
  }

  // Every repaint this file makes goes through here. `gen` is the cart generation the markup was
  // computed at; the transition runs its update a frame later, so the staleness rule is re-checked
  // at the moment of painting, not only when the pass decided to paint.
  function paint(targets, sections, gen) {
    const apply = () => {
      if (gen !== cartGen) {
        needsFreshPaint = true;
        return;
      }
      render(targets, sections);
    };
    const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!document.startViewTransition || reduced) {
      apply();
      return;
    }
    let before = [];
    let after = [];
    let applied = false;
    try {
      before = nameRows();
      const transition = document.startViewTransition(() => {
        clearNames(before);
        applied = true;
        apply();
        after = nameRows();
      });
      const done = () => {
        clearNames(before);
        clearNames(after);
      };
      if (transition && transition.finished) transition.finished.then(done, done);
    } catch (e) {
      clearNames(before);
      if (!applied) apply();
    }
  }

  // ── the pass ────────────────────────────────────────────────────────────────────────────────

  function addedLines(data) {
    if (!data) return [];
    if (Array.isArray(data.items)) return data.items.map(toLine);
    return data.key ? [toLine(data)] : [];
  }

  async function pass(hint) {
    let cfg = config;
    if (!cfg) return;
    const gen = cartGen;

    let state = null;
    // COVER INSERT: which gifts are owed depends on the cart's tags, which only the server can read,
    // so a dynamic config is re-fetched before judging anything — except on the page's first pass,
    // while no cart event has happened and the page-load copy still describes the cart. A failed
    // fetch changes NOTHING: not knowing what is owed is never a reason to add or remove. `cfg` is
    // rebound so the whole pass judges against the one fresh config (tiers AND state together).
    if (cfg.dynamic && !consoleOverride && !(snapshotValid && gen === 0)) {
      const fresh = await fetchConfig();
      if (!fresh) return;
      cfg = config = fresh;
      state = { total: cfg.total, hasMerch: cfg.hasMerch, lines: cfg.lines };
    } else {
      state = hint && Array.isArray(hint.items) ? stateFromCart(cfg, hint) : null;
      if (!state && snapshotValid) state = { total: cfg.total, hasMerch: cfg.hasMerch, lines: cfg.lines };
      // With the promo off, the guarantee is "gone on the shopper's next page load" (ruling R2):
      // settle from the page-load snapshot or from a cart the theme handed us, but never spend a
      // request hunting for a gift line that the switch says nobody is adding. With the switch ON
      // but no gift tier configured (ruling R10 — May has not added `gift_variant` yet) there is
      // likewise nothing to add, and any gift line the page loaded with is already in the snapshot.
      if (!state && (!cfg.active || !cfg.tiers.length)) return;
      if (!state) state = stateFromCart(cfg, await getCart());
    }

    const desired = cfg.active ? cfg.tiers.filter((tier) => state.hasMerch && state.total >= tier.threshold) : [];
    const wanted = new Map(desired.map((tier) => [tier.variantId, tier]));

    const updates = {};
    const kept = new Set();
    const dropped = [];
    state.lines.forEach((line) => {
      const tier = wanted.get(line.variantId);
      // A gift line that is not free is a charge, not a reward — it goes, whatever the tier says.
      const priced = !cfg.allowNonFreeGift && line.price !== 0;
      if (tier && !priced && !kept.has(line.variantId)) {
        kept.add(line.variantId);
        if (line.quantity !== tier.quantity) updates[line.key] = tier.quantity;
        return;
      }
      updates[line.key] = 0;
      if (tier && priced) failedTiers.add(tier.id);
      else if (!tier) dropped.push(line.title);
    });

    // From `wanted`, not `desired`: two tiers pointing at the SAME gift variant would otherwise
    // both be added in one request and Shopify would merge them into a line of quantity 2.
    const adds = breaker ? [] : [...wanted.values()].filter((tier) => !kept.has(tier.variantId) && !failedTiers.has(tier.id));
    // Fast path: the cart already holds exactly the gift lines it should. Zero writes.
    if (!Object.keys(updates).length && !adds.length) return;

    const targets = renderTargets();
    const sections = targets.map((target) => target.section).join(',');
    const sectionsUrl = window.location.pathname;
    let last = null;

    if (Object.keys(updates).length) {
      const result = await post(UPDATE_URL, { updates, sections, sections_url: sectionsUrl });
      // A removal that did not land means the state this pass is holding is wrong (a stale key
      // from another tab, a dropped request). Do not add on top of it, and do not announce a
      // removal that did not happen — the next trigger re-reads the cart and repairs.
      if (!result.ok) return;
      last = result.data;
    }

    let added = [];
    if (adds.length) {
      const body = (tiers) => ({
        items: tiers.map((tier) => ({ id: tier.variantId, quantity: tier.quantity })),
        sections,
        sections_url: sectionsUrl
      });
      let accepted = false;
      const result = await post(ADD_URL, body(adds));
      if (result.ok) {
        accepted = true;
        added = addedLines(result.data);
        last = result.data || last;
      } else if (adds.length === 1) {
        markFailed(adds[0], result.status);
      } else {
        // /cart/add.js is atomic: one sold-out gift 422s the whole batch. Retry per tier so one
        // empty pool cannot starve the others.
        for (const tier of adds) {
          const single = await post(ADD_URL, body([tier]));
          if (single.ok) {
            accepted = true;
            added = added.concat(addedLines(single.data));
            last = single.data || last;
          } else {
            markFailed(tier, single.status);
          }
        }
      }
      if (accepted && added.length === 0 && !cfg.allowNonFreeGift) {
        // No line data came back (unexpected response shape): re-read rather than leave a gift
        // line unverified.
        const ids = new Set(adds.map((tier) => tier.variantId));
        added = stateFromCart(cfg, await getCart()).lines.filter((line) => ids.has(line.variantId));
      }
    }

    // Post-add verification. A priced gift line is taken straight back out and stops this page
    // load from adding anything further. A quantity above what the tier owes means a second actor
    // put the same gift in (a parallel tab, or a trigger whose cart hint predated this add) —
    // /cart/add.js reports "the new quantity for that cart line item", so clamp it here rather than
    // leave a shopper holding extra free gifts until some later trigger happens to run.
    const owed = (line) => (wanted.get(line.variantId) || { quantity: 1 }).quantity;
    const priced = cfg.allowNonFreeGift ? [] : added.filter((line) => line.price !== 0);
    const overQty = added.filter((line) => line.quantity > owed(line) && priced.indexOf(line) === -1);
    if (priced.length || overQty.length) {
      if (priced.length) breaker = true;
      const undo = {};
      priced.forEach((line) => {
        undo[line.key] = 0;
      });
      overQty.forEach((line) => {
        undo[line.key] = owed(line);
      });
      const result = await post(UPDATE_URL, { updates: undo, sections, sections_url: sectionsUrl });
      if (result.ok) last = result.data || last;
    }

    // Only paint if nothing re-rendered the cart while this pass was in flight: these sections
    // were computed before that change, so painting them would put the stale markup back.
    if (last) {
      if (gen === cartGen) paint(targets, last.sections, gen);
      // MPC: G5 — the cart moved under this pass, so its markup is stale and is dropped; but then
      // this pass's OWN mutation (a gift added or removed) is on no surface, and the rerun usually
      // finds nothing to do and exits without painting. Owe one fresh paint (freshPaint, below).
      else needsFreshPaint = true;
    }

    const announcements = added
      .filter((line) => priced.indexOf(line) === -1)
      .map((line) => message(cfg.strings.giftAdded, (wanted.get(line.variantId) || {}).label || line.title))
      // Only while the promo is running: with the switch off this pass is a teardown, and
      // "no longer qualifies for it" would be a false explanation of why the gift went.
      .concat(cfg.active ? dropped.map((title) => message(cfg.strings.giftRemoved, title)) : [])
      .filter(Boolean);
    announce(announcements.join(' '));
  }

  // ── scheduling ──────────────────────────────────────────────────────────────────────────────

  // Single flight. A trigger that lands mid-pass sets the rerun flag instead of queueing N passes;
  // the rerun re-reads the cart, so it can never act on the state the earlier pass just changed.
  // MPC: G5 — runs after EVERY pass, whichever exit it took. Observed on the v8 draft 2026-09-20:
  // cart:refresh then the reconcile's line-item:change:end bumped cartGen mid-pass, the gift went
  // into the server cart and onto neither surface. One GET through the cart coordinator (fresh
  // sections, no mutation, no event — render() never dispatches), painted only if the cart is
  // still where it was when the GET was issued; otherwise the debt stays for the next pass.
  async function freshPaint() {
    if (!needsFreshPaint || !window.CstCart) return;
    const gen = cartGen;
    const targets = renderTargets();
    const result = await window.CstCart.refresh({ sections: targets.map((target) => target.section) });
    if (result && result.reconciled && gen === cartGen) {
      needsFreshPaint = false;
      paint(targets, result.sections, gen);
    }
  }

  function settle(hint) {
    if (running) {
      rerun = true;
      return chain;
    }
    running = true;
    chain = chain
      .then(() => pass(hint))
      .then(() => freshPaint())
      .catch(() => {})
      .finally(() => {
        running = false;
        snapshotValid = false;
        if (rerun) {
          rerun = false;
          settle();
        }
      });
    return chain;
  }

  window.__cstGiftCart = {
    settle,
    // Ruling R11 — replace the config from the console and re-run. Page state is reset with it so
    // a test run is not blocked by a breaker the previous config tripped.
    configure(next) {
      config = normalise(next);
      consoleOverride = true;
      snapshotValid = false;
      breaker = false;
      failedTiers.clear();
      return settle();
    },
    get config() {
      return config;
    },
    get state() {
      return { breaker, failedTiers: [...failedTiers], snapshotValid };
    }
  };

  config = readConfig();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => settle());
  } else {
    settle();
  }

  // Every way this theme's cart changes. All four are dispatched on `document`
  // (app.js:8 dispatchCustomEvent; product.js:1132 bubbles from documentElement). The first three
  // are dispatched AFTER the theme has re-rendered the cart markup (app.js:217/363,
  // product.js:1140), so each one makes any in-flight pass's sections stale — hence the bump.
  // cart-drawer:open does not bump: it also fires on a plain toggle click that renders nothing.
  document.addEventListener('line-item:change:end', (event) => {
    cartGen += 1;
    settle(event.detail && event.detail.cart);
  });
  document.addEventListener('cart:item-added', () => {
    cartGen += 1;
    settle();
  });
  document.addEventListener('cart:refresh', () => {
    cartGen += 1;
    settle();
  });
  document.addEventListener('cart-drawer:open', () => settle());
})();
